import type { MaxDiffDesign, OptionId } from "../types";
import { shuffleArray } from "@/utils/helpers";
import seedrandom from "seedrandom";

// # of total screens we need to show
export const computeScreenCount = ({
  itemCount,
  perScreen,
  targetAppearances,
}: {
  itemCount: number;
  perScreen: number;
  targetAppearances: number;
}): number =>
  Math.max(1, Math.ceil((itemCount * targetAppearances) / perScreen));

/**
 * Generates a per-respondent max diff design: a sequence of screens where each
 * screen is a subset of items of size `perScreen`.
 *
 * Guarantees:
 *   - No duplicate item within a single screen.
 *   - Each item appears floor(screens*perScreen / items) or +1 times (exact frequency balance).
 */
export const generateMaxDiffDesign = ({
  itemIds,
  perScreen,
  targetAppearances = 3,
  seed,
}: {
  itemIds: OptionId[];
  perScreen: number;
  targetAppearances?: number;
  seed?: number;
}): MaxDiffDesign => {
  if (itemIds.length === 0) return { screens: [] };
  if (perScreen < 1) throw new Error("perScreen must be >= 1");

  const uniqueItemIds = Array.from(new Set(itemIds));
  const effectivePerScreen = Math.min(perScreen, uniqueItemIds.length);
  const rng = seedrandom(
    String(seed ?? Math.floor(Math.random() * 0xffffffff))
  );
  const screens = computeScreenCount({
    itemCount: uniqueItemIds.length,
    perScreen: effectivePerScreen,
    targetAppearances,
  });
  const totalSlots = screens * effectivePerScreen;

  // Build a bag with each item appearing `targetAppearances` times, then pad
  // with the items that have the fewest entries so far until the bag fills
  // exactly totalSlots.
  const bag: OptionId[] = [];
  const counts = new Map<OptionId, number>();
  for (const id of uniqueItemIds) counts.set(id, 0);
  for (let r = 0; r < targetAppearances; r++) {
    for (const id of uniqueItemIds) {
      bag.push(id);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  while (bag.length < totalSlots) {
    // deterministic tie-break: iterate itemIds and pick the first with minimum count
    let minCount = Infinity;
    let minId: OptionId | null = null;
    for (const id of uniqueItemIds) {
      const c = counts.get(id) ?? 0;
      if (c < minCount) {
        minCount = c;
        minId = id;
      }
    }
    if (minId === null) break;
    bag.push(minId);
    counts.set(minId, minCount + 1);
  }
  if (bag.length > totalSlots) bag.length = totalSlots;

  const shuffledBag = shuffleArray(bag, rng);

  // Pass 1: chunk the bag as-is.
  const out: OptionId[][] = [];
  for (let i = 0; i < shuffledBag.length; i += effectivePerScreen) {
    out.push(
      shuffledBag.slice(i, Math.min(i + effectivePerScreen, shuffledBag.length))
    );
  }

  // Pass 2: fix within-screen duplicates by pairwise swapping between screens.
  // Count balance is preserved because every swap exchanges one slot in screen
  // A with one slot in screen B: the global item counts don't change.
  const canAvoidDuplicates = uniqueItemIds.length >= effectivePerScreen;
  if (canAvoidDuplicates) {
    const findDupIndex = (screen: OptionId[]): number => {
      const seen = new Set<OptionId>();
      for (let i = 0; i < screen.length; i++) {
        if (seen.has(screen[i])) return i;
        seen.add(screen[i]);
      }
      return -1;
    };

    // Try swapping the duplicate slot in screen A with any slot in screen B
    // such that the swap removes the duplicate from A without creating one in B.
    const tryFixDuplicate = (
      screens: OptionId[][],
      aIdx: number,
      aSlotIdx: number
    ): boolean => {
      const dupItem = screens[aIdx][aSlotIdx];
      const aItems = new Set(screens[aIdx]);
      for (let bIdx = 0; bIdx < screens.length; bIdx++) {
        if (bIdx === aIdx) continue;
        const bScreen = screens[bIdx];
        const bItems = new Set(bScreen);
        if (bItems.has(dupItem)) continue;
        for (let bSlotIdx = 0; bSlotIdx < bScreen.length; bSlotIdx++) {
          const candidate = bScreen[bSlotIdx];
          if (candidate === dupItem) continue;
          if (aItems.has(candidate)) continue;
          // swap
          screens[aIdx][aSlotIdx] = candidate;
          screens[bIdx][bSlotIdx] = dupItem;
          return true;
        }
      }
      return false;
    };

    // Multiple passes in case a single swap creates a dup we hadn't seen yet.
    // Converges quickly for well-sized bags; safety bound on iterations.
    const maxPasses = 10;
    for (let pass = 0; pass < maxPasses; pass++) {
      let fixedAny = false;
      let remainingDupsAfterPass = 0;
      for (let s = 0; s < out.length; s++) {
        let dupIdx = findDupIndex(out[s]);
        while (dupIdx !== -1) {
          const fixed = tryFixDuplicate(out, s, dupIdx);
          if (!fixed) {
            remainingDupsAfterPass++;
            break;
          }
          fixedAny = true;
          dupIdx = findDupIndex(out[s]);
        }
      }
      if (!fixedAny || remainingDupsAfterPass === 0) break;
    }
  }

  // Shuffle within each screen for positional balance.
  const shuffledOut = out.map((screen) => shuffleArray(screen, rng));

  return { screens: shuffledOut };
};
